import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Save, Plus, Trash2 } from "lucide-react";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { VenueImageManager } from "@/components/admin/VenueImageManager";
import { getUserFriendlyError } from "@/lib/error-handler";

const VENUE_TYPES = ["sala", "teatro", "auditorio", "exterior"] as const;
const VENUE_STATUSES = ["active", "draft", "archived"] as const;
const DAYS = ["lunes", "martes", "miércoles", "jueves", "viernes", "sábado", "domingo"];

interface RateRow {
  id?: string;
  name: string;
  price: string;
  applicable_days: string[];
  holiday_eve: boolean;
  time_slot: string;
  notes: string;
  is_active: boolean;
  sort_order: number;
  _delete?: boolean;
}

const emptyRate: RateRow = {
  name: "", price: "", applicable_days: [], holiday_eve: false,
  time_slot: "", notes: "", is_active: true, sort_order: 0,
};

export default function AdminVenueEdit() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<Record<string, any>>({});
  const [rates, setRates] = useState<RateRow[]>([]);
  const [dirty, setDirty] = useState(false);

  // Load venue
  const { data: venue, isLoading } = useQuery({
    queryKey: ["admin-venue-edit", id],
    queryFn: async () => {
      const { data, error } = await supabase.from("venues").select("*").eq("id", id!).single();
      if (error) throw error;
      return data;
    },
    enabled: !isNew,
  });

  // Load rates
  const { data: existingRates } = useQuery({
    queryKey: ["admin-venue-rates", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("venue_rates" as any)
        .select("*")
        .eq("venue_id", id!)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data as any[];
    },
    enabled: !isNew,
  });

  // Init form
  useEffect(() => {
    if (venue) {
      setForm({ ...venue });
    } else if (isNew) {
      setForm({
        name: "", city: "", autonomous_community: "", address_street: "", address_number: "",
        postal_code: "", capacity: null, official_price: null, official_price_notes: "",
        email: "", phone: "", website: "", instagram: "", type: "sala", status: "active",
        description: "",
        backline_available: null, backline_url: "", backline_details: "",
        rider_available: null, rider_url: "", rider_details: "",
        audio_recording: null, poster_design: null, merch_area_official: null,
        ticketing_type: null, ticketing_company: "",
        sound_tech_official: null,
      });
    }
  }, [venue, isNew]);

  useEffect(() => {
    if (existingRates) {
      setRates(existingRates.map((r: any) => ({
        id: r.id, name: r.name, price: String(r.price),
        applicable_days: r.applicable_days ?? [], holiday_eve: r.holiday_eve ?? false,
        time_slot: r.time_slot ?? "", notes: r.notes ?? "",
        is_active: r.is_active ?? true, sort_order: r.sort_order ?? 0,
      })));
    }
  }, [existingRates]);

  // Unsaved changes warning
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirty) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const set = useCallback((key: string, value: any) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  }, []);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (goBack: boolean) => {
      const { id: _id, created_at, updated_at, slug, ...venueData } = form;

      // Clean empty strings to null
      const cleaned: Record<string, any> = {};
      for (const [key, value] of Object.entries(venueData)) {
        cleaned[key] = value === "" || value === undefined ? null : value;
      }
      cleaned.name = form.name;
      cleaned.city = form.city;

      let venueId = id;

      if (isNew) {
        const { data, error } = await supabase.from("venues").insert(cleaned as any).select("id").single();
        if (error) throw error;
        venueId = data.id;
      } else {
        const { error } = await supabase.from("venues").update(cleaned as any).eq("id", id!);
        if (error) throw error;
      }

      // Save rates
      const toDelete = rates.filter(r => r._delete && r.id);
      const toUpsert = rates.filter(r => !r._delete);

      for (const r of toDelete) {
        await supabase.from("venue_rates" as any).delete().eq("id", r.id!);
      }

      for (let i = 0; i < toUpsert.length; i++) {
        const r = toUpsert[i];
        const payload: any = {
          venue_id: venueId,
          name: r.name,
          price: parseFloat(r.price) || 0,
          applicable_days: r.applicable_days.length ? r.applicable_days : null,
          holiday_eve: r.holiday_eve,
          time_slot: r.time_slot || null,
          notes: r.notes || null,
          is_active: r.is_active,
          sort_order: i,
        };
        if (r.id) {
          await supabase.from("venue_rates" as any).update(payload).eq("id", r.id);
        } else if (r.name && r.price) {
          await supabase.from("venue_rates" as any).insert(payload);
        }
      }

      return { goBack, venueId };
    },
    onSuccess: ({ goBack, venueId }) => {
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ["admin-venues"] });
      queryClient.invalidateQueries({ queryKey: ["venues"] });
      toast({ title: isNew ? "Sala creada" : "Sala actualizada" });
      if (goBack) {
        navigate("/admin/venues");
      } else if (isNew && venueId) {
        navigate(`/admin/venues/${venueId}/edit`, { replace: true });
      }
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: getUserFriendlyError(err), variant: "destructive" });
    },
  });

  if (!isNew && isLoading) {
    return <AdminLayout requiredModule="salas" requiredPermission="can_edit"><Skeleton className="h-96" /></AdminLayout>;
  }

  if (!form.name && form.name !== "" && !isNew) {
    return <AdminLayout requiredModule="salas" requiredPermission="can_edit"><p>Sala no encontrada</p></AdminLayout>;
  }

  return (
    <AdminLayout requiredModule="salas" requiredPermission="can_edit">
      <div className="pb-24">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin/venues")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="font-display text-2xl font-bold">
            {isNew ? "Nueva Sala" : `Editar: ${form.name}`}
          </h1>
        </div>

        <Accordion type="multiple" defaultValue={["basic", "contact", "services", "rates", "description", "images"]} className="space-y-3">
          {/* 1. Datos básicos */}
          <AccordionItem value="basic" className="rounded-lg border px-4">
            <AccordionTrigger className="text-base font-semibold">Datos básicos</AccordionTrigger>
            <AccordionContent>
              <div className="grid gap-4 sm:grid-cols-2 py-2">
                <div className="sm:col-span-2">
                  <Label>Nombre *</Label>
                  <Input value={form.name ?? ""} onChange={e => set("name", e.target.value)} />
                </div>
                <div>
                  <Label>Ciudad *</Label>
                  <Input value={form.city ?? ""} onChange={e => set("city", e.target.value)} />
                </div>
                <div>
                  <Label>Comunidad Autónoma</Label>
                  <Input value={form.autonomous_community ?? ""} onChange={e => set("autonomous_community", e.target.value)} />
                </div>
                <div>
                  <Label>Tipo</Label>
                  <Select value={form.type ?? "sala"} onValueChange={v => set("type", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VENUE_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Estado</Label>
                  <Select value={form.status ?? "active"} onValueChange={v => set("status", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VENUE_STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Aforo</Label>
                  <Input type="number" value={form.capacity ?? ""} onChange={e => set("capacity", e.target.value ? parseInt(e.target.value) : null)} />
                </div>
                <div>
                  <Label>Precio Oficial base (€)</Label>
                  <Input type="number" value={form.official_price ?? ""} onChange={e => set("official_price", e.target.value ? parseFloat(e.target.value) : null)} />
                </div>
                <div className="sm:col-span-2">
                  <Label>Notas Precio</Label>
                  <Input value={form.official_price_notes ?? ""} onChange={e => set("official_price_notes", e.target.value)} />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 2. Contacto y ubicación */}
          <AccordionItem value="contact" className="rounded-lg border px-4">
            <AccordionTrigger className="text-base font-semibold">Contacto y ubicación</AccordionTrigger>
            <AccordionContent>
              <div className="grid gap-4 sm:grid-cols-2 py-2">
                <div><Label>Calle</Label><Input value={form.address_street ?? ""} onChange={e => set("address_street", e.target.value)} /></div>
                <div><Label>Número</Label><Input value={form.address_number ?? ""} onChange={e => set("address_number", e.target.value)} /></div>
                <div><Label>Código Postal</Label><Input value={form.postal_code ?? ""} onChange={e => set("postal_code", e.target.value)} /></div>
                <div><Label>Email</Label><Input type="email" value={form.email ?? ""} onChange={e => set("email", e.target.value)} /></div>
                <div><Label>Teléfono</Label><Input value={form.phone ?? ""} onChange={e => set("phone", e.target.value)} /></div>
                <div><Label>Web</Label><Input value={form.website ?? ""} onChange={e => set("website", e.target.value)} /></div>
                <div><Label>Instagram</Label><Input value={form.instagram ?? ""} onChange={e => set("instagram", e.target.value)} /></div>
                <div className="sm:col-span-2">
                  <Label>Consultar agenda (URL)</Label>
                  <Input value={form.consultar_agenda_url ?? ""} onChange={e => set("consultar_agenda_url", e.target.value)} placeholder="https://..." />
                  <p className="text-xs text-muted-foreground mt-1">Si lo dejas vacío, se usará el Sitio web de la sala.</p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 3. Servicios */}
          <AccordionItem value="services" className="rounded-lg border px-4">
            <AccordionTrigger className="text-base font-semibold">Servicios</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-6 py-2">
                {/* Backline / Rider */}
                <ServiceToggle label="Backline / Rider disponible" value={form.backline_available} onChange={v => { set("backline_available", v); set("rider_available", v); }}>
                  <p className="text-xs text-muted-foreground mt-1">Si lo dejas en blanco, se mostrará como Desconocido. Si existe un enlace antiguo de Rider, se conserva por defecto.</p>
                  <div className="grid gap-3 sm:grid-cols-2 mt-3">
                    <div>
                      <Label className="text-xs">Enlace (opcional)</Label>
                      <Input value={form.backline_url ?? ""} onChange={e => set("backline_url", e.target.value)} placeholder="https://..." />
                      <p className="text-[11px] text-muted-foreground mt-1">Enlace a la página oficial con el backline o el rider.</p>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Detalles (opcional)</Label>
                      <RichTextEditor content={form.backline_details ?? ""} onChange={v => set("backline_details", v)} placeholder="Ej: Batería disponible, amplificadores, tarifas, condiciones…" />
                    </div>
                  </div>
                </ServiceToggle>

                {/* Simple toggles */}
                <ServiceToggle label="Grabación de audio" value={form.audio_recording} onChange={v => set("audio_recording", v)} />
                <ServiceToggle label="Diseño de cartelería" value={form.poster_design} onChange={v => set("poster_design", v)} />
                <ServiceToggle label="Zona de merchandising" value={form.merch_area_official} onChange={v => set("merch_area_official", v)} />
                <ServiceToggle label="Técnico de sonido" value={form.sound_tech_official} onChange={v => set("sound_tech_official", v)} />

                {/* Ticketing */}
                <div className="rounded-lg border p-4">
                  <Label className="text-sm font-medium">Ticketing</Label>
                  <Select value={form.ticketing_type ?? "none"} onValueChange={v => set("ticketing_type", v === "none" ? null : v)}>
                    <SelectTrigger className="mt-2"><SelectValue placeholder="Sin dato" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Desconocido</SelectItem>
                      <SelectItem value="obligatorio">Propio (obligatorio)</SelectItem>
                      <SelectItem value="libre">Libre</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.ticketing_type && (
                    <div className="mt-3">
                      <Label className="text-xs">Empresa de ticketing</Label>
                      <Input value={form.ticketing_company ?? ""} onChange={e => set("ticketing_company", e.target.value)} />
                    </div>
                  )}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 4. Tarifas oficiales */}
          <AccordionItem value="rates" className="rounded-lg border px-4">
            <AccordionTrigger className="text-base font-semibold">Tarifas oficiales</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4 py-2">
                {rates.filter(r => !r._delete).map((rate, idx) => (
                  <Card key={idx} className="border-dashed">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Tarifa {idx + 1}</span>
                        <div className="flex items-center gap-2">
                          <Label className="text-xs">Activa</Label>
                          <Switch checked={rate.is_active} onCheckedChange={v => {
                            const copy = [...rates]; copy[rates.indexOf(rate)] = { ...rate, is_active: v }; setRates(copy); setDirty(true);
                          }} />
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => {
                            if (rate.id) {
                              const copy = [...rates]; copy[rates.indexOf(rate)] = { ...rate, _delete: true }; setRates(copy);
                            } else {
                              setRates(rates.filter((_, i) => i !== rates.indexOf(rate)));
                            }
                            setDirty(true);
                          }}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div><Label className="text-xs">Nombre *</Label><Input value={rate.name} onChange={e => {
                          const copy = [...rates]; copy[rates.indexOf(rate)] = { ...rate, name: e.target.value }; setRates(copy); setDirty(true);
                        }} placeholder="Ej: Entre semana" /></div>
                        <div><Label className="text-xs">Precio (€) *</Label><Input type="number" value={rate.price} onChange={e => {
                          const copy = [...rates]; copy[rates.indexOf(rate)] = { ...rate, price: e.target.value }; setRates(copy); setDirty(true);
                        }} /></div>
                      </div>
                      <div>
                        <Label className="text-xs mb-2 block">Días aplicables</Label>
                        <div className="flex flex-wrap gap-2">
                          {DAYS.map(day => (
                            <label key={day} className="flex items-center gap-1.5 text-xs cursor-pointer">
                              <Checkbox
                                checked={rate.applicable_days.includes(day)}
                                onCheckedChange={checked => {
                                  const copy = [...rates];
                                  const rateIdx = rates.indexOf(rate);
                                  const days = checked
                                    ? [...rate.applicable_days, day]
                                    : rate.applicable_days.filter(d => d !== day);
                                  copy[rateIdx] = { ...rate, applicable_days: days };
                                  setRates(copy); setDirty(true);
                                }}
                              />
                              <span className="capitalize">{day}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Label className="text-xs">Víspera de festivo</Label>
                        <Switch checked={rate.holiday_eve} onCheckedChange={v => {
                          const copy = [...rates]; copy[rates.indexOf(rate)] = { ...rate, holiday_eve: v }; setRates(copy); setDirty(true);
                        }} />
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div><Label className="text-xs">Franja horaria</Label><Input value={rate.time_slot} onChange={e => {
                          const copy = [...rates]; copy[rates.indexOf(rate)] = { ...rate, time_slot: e.target.value }; setRates(copy); setDirty(true);
                        }} /></div>
                        <div><Label className="text-xs">Notas</Label><Input value={rate.notes} onChange={e => {
                          const copy = [...rates]; copy[rates.indexOf(rate)] = { ...rate, notes: e.target.value }; setRates(copy); setDirty(true);
                        }} /></div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => {
                  setRates([...rates, { ...emptyRate, sort_order: rates.length }]);
                  setDirty(true);
                }}>
                  <Plus className="mr-2 h-4 w-4" /> Añadir tarifa
                </Button>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 5. Descripción */}
          <AccordionItem value="description" className="rounded-lg border px-4">
            <AccordionTrigger className="text-base font-semibold">Descripción</AccordionTrigger>
            <AccordionContent>
              <div className="py-2">
                <RichTextEditor
                  content={form.description ?? ""}
                  onChange={v => set("description", v)}
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 6. Imágenes */}
          {!isNew && (
            <AccordionItem value="images" className="rounded-lg border px-4">
              <AccordionTrigger className="text-base font-semibold">Imágenes</AccordionTrigger>
              <AccordionContent>
                <div className="py-2">
                  <VenueImageManager venueId={id!} />
                </div>
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>

        {/* Sticky save bar */}
        <div className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur px-4 py-3">
          <div className="container flex justify-end gap-3">
            <Button variant="outline" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate(true)}>
              <Save className="mr-2 h-4 w-4" />
              {saveMutation.isPending ? "Guardando…" : "Guardar y volver"}
            </Button>
            <Button disabled={saveMutation.isPending} onClick={() => saveMutation.mutate(false)}>
              <Save className="mr-2 h-4 w-4" />
              {saveMutation.isPending ? "Guardando…" : "Guardar y seguir editando"}
            </Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

// -- Service toggle helper --
function ServiceToggle({
  label, value, onChange, children,
}: {
  label: string;
  value: boolean | null;
  onChange: (v: boolean | null) => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border p-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{label}</Label>
        <div className="flex items-center gap-2">
          <Button type="button" variant={value === true ? "default" : "outline"} size="sm" className="h-7 text-xs"
            onClick={() => onChange(value === true ? null : true)}>Sí</Button>
          <Button type="button" variant={value === false ? "default" : "outline"} size="sm" className="h-7 text-xs"
            onClick={() => onChange(value === false ? null : false)}>No</Button>
        </div>
      </div>
      {value === true && children}
    </div>
  );
}
